/** English strings for the expert slice (namespace: expert.*). */
export const expertEn = {
  title: "Expert directory",
  subtitle: "Validated sustainable-development experts for this action-plan action.",
  loading: "Loading…",
  error: "Something went wrong.",
  retry: "Try again",

  tabs: {
    directory: "Directory",
    favorites: "Favorites",
    register: "Registration",
    dashboard: "My profile",
  },


  serviceTypes: {
    analysis: "Analysis, study (market, feasibility), diagnostic",
    coaching: "Support and advisory services (coaching, mentoring)",
    strategic_planning: "Strategic planning, action plan, consultation",
    business_startup: "Business creation, establishment and start-up",
    infrastructure:
      "Infrastructure (upgrading, expansion, renovation, construction)",
    technology_adoption: "Technology acquisition and implementation",
    commercialization: "Commercialization of a product or technology",
    market_development: "Market expansion and development",
    technology_demo:
      "Demonstration, technology showcase, technology maturation projects",
    certification: "Approval or certification",
    audit: "Audit and verification",
    investment_support: "Support for investment projects",
    research_development: "Research and development, innovation",
    training: "Tool development, training and awareness",
    turnaround: "Turnaround and retention of strategic businesses",
    hiring_support: "Financial support for hiring professionals",
    other: "Other (please specify)",
  },

  directory: {
    heading: "Expert directory",
    help: "Validated experts who can support you on this action plan action, ranked by how well they fit your enterprise.",
    resultCount_one: "{{count}} expert found",
    resultCount_other: "{{count}} experts found",
    favoriteCount_one: "{{count}} favorite expert",
    favoriteCount_other: "{{count}} favorite experts",
    tierNavLabel: "Relevance tiers",
    bestMatch: "Best match",
    seeMore_one: "See {{count}} more expert",
    seeMore_other: "See {{count}} more experts",
    none: "No experts are available for this action yet.",
    noFavorites: "You haven't saved any experts for this action yet.",
    seeAll: "See all experts",
    unavailable: "The expert directory isn't available for this action.",
    disabled: "The expert directory isn't available on your account.",
    actionRequired: {
      title: "Open the directory from an action",
      help: "The expert directory opens from an action in your action plan. Go to your action plans and click “Find experts” under an eligible action.",
      goToPlans: "Go to action plans",
    },
  },

  filters: {
    heading: "Filter",
    panelToggle: "Filters",
    search: "Name, organization…",
    searchLabel: "Search for an expert",
    originRegions: "Regions of origin",
    serviceTypes: "Types of services offered",
    serviceRegions: "Regions served",
    sectors: "Business sectors served",
    orgTypes: "Organization types served",
    favoritesOnly: "My favorites only",
    any: "Any region",
    clear: "Reset",
  },

  card: {
    viewProfile: "View profile",
    favorite: "Save to favorites",
    unfavorite: "Remove from favorites",
    website: "Website",
    serves: "Regions served",
    regionUnknown: "Region not specified",
    servesRegions_one: "{{count}} region served",
    servesRegions_other: "{{count}} regions served",
    moreRegions_one: "+{{count}} more",
    moreRegions_other: "+{{count}} more",
    // Printed before each region list, as Django's card and profile hero do.
    originLabel_one: "Region of origin:",
    originLabel_other: "Regions of origin:",
    servedLabel_one: "Region served:",
    servedLabel_other: "Regions served:",
  },

  profile: {
    back: "Back to directory",
    presentation: "About",
    coveredIssues: "Issues covered in the Activateur",
    overview: "Overview",
    contact: "Contact",
    contactPerson: "Contact person",
    contactCta: "Contact this expert",
    email: "Email",
    phone: "Phone",
    extension: "ext. {{ext}}",
    website: "Website",
    headOffice: "Head office",
    region: "Region",
    address: "Address",
    originRegions: "Regions of origin",
    servedRegions: "Regions served",
    serviceTypes: "Types of services offered",
    sectors: "Business sectors served",
    orgTypes: "Organization types served",
    founded: "Founded",
    ddStart: "Sustainable-development services since",
    // Wording matched to the Django profile's msgids — the same figure must not
    // be labelled two ways across the apps.
    employees: "Number of employees",
    dedicated: "Dedicated experts",
    awards: "Awards and distinctions",
    affiliations: "Professional order of affiliation",
    expertises: "Areas of expertise",
    mandates: "Reference mandates",
    mandateCount_one: "{{count}} mandate",
    mandateCount_other: "{{count}} mandates",
    goals: "Goals",
    noExpertises: "No declared areas of expertise.",
    noMandates: "No reference mandates listed.",
    notAvailable: "This expert profile isn't available.",
  },

  favorites: {
    heading: "Saved experts",
    help: "Experts you've saved across your action plan actions.",
    empty: "You haven't saved any experts yet.",
    emptyHint: "Save experts from the directory to compare them later.",
    remove: "Remove",
    removed: "Removed from favorites",
    savedFor: "Saved for an action",
  },

  dashboard: {
    heading: "My expert space",
    notFound:
      "You don't have a finalized expert profile yet. Complete registration to appear in the directory.",
    goToRegister: "Start registration",
    profileEyebrow: "My profile in detail",
    viewPublic: "View my public profile",
    completeProfile: "Select my objectives and actions",
    editObjectives: "Edit my objectives",
    edit: "Edit my profile",
    editResubmit: "Correct and resubmit",
    editMenu: {
      info: "My information",
      expertise: "My areas of expertise",
      goals: "My objectives",
    },
    lastStep: {
      title: "Last step",
      body: "Your registration is approved. Select your objectives and courses of action to make your profile visible in the directory.",
      newIssueBody:
        "Your new issue is approved. Select its objectives and courses of action to show them in the directory.",
    },
    lead: {
      submitted: "Your application is being reviewed by the CQDD.",
      validated: "Your profile is published in the Activateur expert directory.",
      approved: "Your registration is approved.",
      validatedPending:
        "Your changes are awaiting validation. Your validated profile stays visible in the meantime.",
      rejected: "Your application was not accepted by the CQDD.",
    },
    banner: {
      pendingLabel: "Changes pending",
      pendingBody:
        "The CQDD will review your changes. No action is required on your part.",
      rejectedLabel: "Message from the CQDD",
    },
    stats: {
      enjeux: "Areas of expertise",
      actions: "Courses of action",
      regions: "Regions served",
      mandates: "Reference mandates",
    },
    status: {
      label: "Status",
      draft: "Draft",
      submitted: "Submitted",
      validated: "Validated",
      rejected: "Rejected",
    },
    detail: {
      identity: "Identity and contacts",
      org: "Organization profile",
      scope: "Service scope",
      expertises: "Expertise, courses of action and mandates",
      legalName: "Legal name (REQ)",
      neq: "NEQ",
      email: "Email",
      phone: "Phone",
      website: "Website",
      headOffice: "Head office",
      otherLocations: "Other places of business",
      otherProvinces: "Other provinces and territories",
      primaryContact: "Main respondent",
      secondaryContact: "Secondary respondent",
      foundingYear: "Year founded",
      ddStart: "SD services start",
      employees: "Number of employees",
      dedicated: "Experts dedicated to SD",
      awards: "Awards and distinctions",
      affiliations: "Professional order of affiliation",
      regions: "Regions served",
      sectors: "Business sectors served",
      types: "Organization types served",
      objectives: "Objectives and courses of action",
      noActions: "No course of action selected",
      mandate: "Reference mandate",
      preapproved: "Pre-approved by CQDD",
      clients: "Client contact details",
      noName: "Unnamed client",
      mandateCount_one: "{{count}} mandate completed",
      mandateCount_other: "{{count}} mandates completed",
      noExpertise: "No expertise declared.",
      enjeuxCount_one: "{{count}} issue",
      enjeuxCount_other: "{{count}} issues",
      none: "—",
    },
  },

  objectives: {
    heading: "Objectives and courses of action",
    help: "Your registration is approved. For each declared issue, choose the objectives you offer services for and the courses of action you can contribute to. This completes your profile and makes it visible in the directory.",
    backToDashboard: "Back to my dashboard",
  },

  register: {
    heading: "Become a listed expert",
    help: "Register your organization to appear in the sustainable-development expert directory. Your answers are saved at each step.",
    loadError: "Could not load your registration. Please try again later.",
    submittedTitle: "Your request has been submitted",
    submittedLead: "Your request will be reviewed by the CQDD.",
    submittedNote:
      "The CQDD may contact you for further information. If your request is approved, you will receive an email allowing you to select your objectives and action items to help the SMEs using the Activateur.",
    goToDashboard: "Access my dashboard",
    save: "Save",
    saveChanges: "Save changes",
    stepLabel: "Step {{current}} of {{total}}",
    steps: {
      org: "Organization",
      serviceRange: "Service scope",
      expertise: "Expertise",
      goals: "Objectives & actions",
    },
    next: "Next",
    back: "Back",
    submit: "Finish & submit",
    submitRequest: "Submit my request",
    declarationTitle: "Validation and declaration",
    confirmSubmit: "Confirm and submit",
    confirmSave: "Confirm and save",
    cancel: "Cancel",
    orgOverviewHeading: "General overview of the organization",
    expertiseHeading: "Expertise — sustainability issues",
    locationHeading: "Location",
    otherPlacesHeading: "Other places of business",
    otherPlacesHelp:
      "Add each other place of business of your organization, with its address, its region and its RCM. Leave empty if you have none.",
    addPlace: "Add a place of business",
    removePlace: "Remove this place of business",
    chooseRegion: "Choose a region",
    chooseMrc: "Choose an RCM",
    mandatoryNote: "All fields marked with * are mandatory.",
    actionsModalIntro: "Select the actions to which you can contribute for this objective.",
    eligibilityHeading: "Eligibility",
    organizationContactHeading: "Organization contact details",
    contactsHeading: "Contact persons",
    contactHeading: "Main respondent",
    contactHeadingSecondary: "Secondary respondent",
    contact: {
      last_name: "Last name",
      first_name: "First name",
      function: "Role",
      email: "Email",
      phone: "Work phone",
      extension: "Ext.",
      linkedin: "LinkedIn profile",
    },
    profileHeading: "Organization description",
    scopeHeading: "Service scope",
    // Still read by the tool and financial-aid admin forms, which keep sizes.
    selectAllSizes: "All sizes",
    scopeIntro: "Indicate the regions, business sectors, and organization types you serve.",
    scopeHelp:
      "These choices define the options offered for each issue in the next step.",
    expertiseHelp:
      "Select the issues you offer services for, specify the scope, and describe a completed mandate for each.",
    profileCompleted:
      "Your profile is complete: your objectives and courses of action are now visible in the directory.",
    pendingIssuesNotice:
      "The objectives and courses of action of your new issues will be offered once the CQDD approves your changes.",
    goalsHelp:
      "For each issue, choose the objectives you target and their courses of action.",
    mandateHeading: "Reference mandate",
    signatureStatement: "I confirm the accuracy of this declaration (electronic signature).",
    preapproved: "Pre-approved by CQDD",
    preapprovedNoMandate:
      "No mandate to describe for this issue: the CQDD has already recognized your expertise.",
    preapprovedNoticeTitle: "Pre-approved issues",
    preapprovedNotice:
      "The CQDD has pre-approved your expertise for: {{names}}. No mandate is asked for these issues. For any other issue you add, describe a completed mandate. Your whole request will be reviewed by the CQDD.",
    noObjectives: "No objectives available for this issue.",
    noActions: "No action is attached to this objective.",
    addClient: "Add a client",
    clientsConfidential:
      "Information for the CQDD's exclusive use, which will not be made public. A client may be contacted to confirm that it is accurate.",
    removeClient: "Remove this client",
    describeMandate: "Describe the mandate",
    editMandate: "Edit",
    mandateModalTitle: "Mandate",
    mandateHelp:
      "Please indicate the number of mandates you have completed for this issue. One is enough to qualify you.",
    mandateDescriptionHelp:
      "Please describe a mandate that illustrates your specific expertise on this issue.",
    mandateIncomplete: "To complete",
    mandateUnnamed: "Untitled mandate",
    actionsLabel: "Actions",
    actionsModalTitle: "Actions",
    done: "Done",
    selectedCount_one: "{{count}} selected",
    selectedCount_other: "{{count}} selected",
    select: "Select",
    modify: "Edit",
    noSelection: "No selection",
    noneSelected: "No option selected",
    selectAll: "Select all",
    selectAllIn: "Select all — {{name}}",
    // Badge the closed control collapses a fully picked group to.
    allOfGroup: "All {{name}}",
    search: "Search…",
    searchIn: "Search in {{name}}",
    noSearchResult: "No result",
    selectAllRegions: "All regions",
    selectAllSectors: "All business sectors",
    selectAllTypes: "All types",
    validateScope: "Confirm the scope for this issue",
    scopeForIssueHelp:
      "Specify, for this issue, the business sectors and organization types you serve, from your choices in the previous step.",
    uncategorized: "Other",
    otherSectors: "Other business sectors served",
    otherTypes: "Other organization types served",
    otherServices: "Other type(s) of services",
    fields: {
      organization_name: "Organization name",
      legal_name_req: "Name in the Quebec Enterprise Register (REQ)",
      neq: "Québec enterprise number (NEQ)",
      website: "Website",
      email: "Organization email",
      phone: "Organization phone number",
      head_office_address: "Your head office address",
      head_office_region: "Head office administrative region",
      head_office_mrc: "Head office RCM",
      place_address: "Place of business address",
      place_region: "Administrative region of the place of business",
      place_mrc: "RCM of the place of business",
      serves_other_provinces:
        "Do you have a place of business in other Canadian provinces or territories?",
      other_provinces: "If so, specify",
      on_rena: "Is your organization on the Register of Enterprises Ineligible for public contracts (RENA)?",
      contact_primary_first_name: "First name",
      contact_primary_last_name: "Last name",
      contact_primary_function: "Role",
      contact_primary_email: "Contact email",
      contact_primary_phone: "Contact phone",
      founding_year: "Year founded",
      dd_services_start_year: "Sustainable development services start (year)",
      total_employees: "Total number of employees",
      dedicated_experts_count:
        "Number of experts dedicated to the sustainable development service offering",
      description: "Organization description (1,000 characters max., spaces included)",
      awards: "Awards and distinctions",
      affiliations: "Professional order of affiliation, if applicable?",
      service_types: "What types of services do you offer?",
      service_regions: "Regions served",
      sectors_served: "Business sectors served",
      organization_types_served: "Organization types served",
      mandate_name: "Mandate title",
      mandate_year: "Year of completion",
      mandate_description:
        "Brief description of the mandate: Describe the main objective, the steps carried out and the deliverables (1,000 characters max., spaces included).",
      mandate_accompaniment:
        "This is an accompaniment mandate (not the sale of a product/software)",
      mandate_count: "Number of mandates completed for this issue",
      mandate_clients: "Client contact details",
      client_org_name: "Organization name",
      client_contact_name: "Contact person",
      client_phone: "Business phone number",
      client_email: "Client email",
      issue_sectors: "Business sectors",
      issue_org_types: "Organization types served",
      consent_display:
        "I consent to the CQDD reviewing my request to confirm my eligibility for the expert directory.",
      declarant_name: "Name",
      declarant_function: "Function",
    },
    yes: "Yes",
    no: "No",
    validation: {
      organization_name: "Organization name is required.",
      no_enjeu: "Select at least one issue.",
      contact_email: "Enter a valid email.",
      required: "This field is required.",
      too_long: "This text cannot exceed {{limit}} characters (spaces included).",
      mrc_region: "This RCM does not belong to the selected region.",
      place_region_required: "Choose the region of this place of business.",
      mrc_required: "Choose the RCM of this place of business.",
      place_mrc_region:
        "This RCM does not belong to the region of this place of business.",
      goal_needs_action: "Select at least one action for this objective.",
      provinces_required: "Choose at least one province or territory.",
      fix_required: "Please fill in all required fields.",
      fix_indicated: "Please correct the indicated fields.",
      rena_ineligible:
        "An organization listed on the RENA is not eligible for the expert directory.",
      mandate_name: "The mandate title is required.",
      mandate_year: "The year of completion is required.",
      mandate_year_range: "Please enter a year between {{min}} and {{max}}.",
      year_range: "Please enter a year between {{min}} and {{max}}.",
      mandate_description: "The mandate description is required.",
      mandate_accompaniment: "Confirmation is required.",
      client_email: "The email address is invalid.",
      client_phone: "The phone number is invalid.",
      phone: "The phone number is invalid.",
    },
  },

  invite: {
    title: "Expert Directory invitation",
    validBody:
      "Your invitation is valid. Create your account to complete your directory registration.",
    cta: "Create my account",
    toLogin: "I already have an account — sign in",
    invalid: "This invitation link is not valid.",
    revoked: "This invitation link is no longer valid.",
    expired: "This invitation link has expired.",
    used: "This invitation has already been used. Please sign in.",
  },

  signup: {
    title: "Create an expert account",
    subtitle: "Create your account to register for the Activateur Expert Directory.",
    password: "Password",
    passwordConfirm: "Confirm password",
    submit: "Create my account",
  },

  emailConfirmation: {
    title: "Registration validation",
    body: "Your account has been created, but you must now validate it by clicking the link in the email we just sent to the address you provided.",
    spamNote:
      "The confirmation email may take several minutes to arrive and could land in your junk mail folder.",
    check: "Check whether my email is confirmed",
    checking: "Checking…",
    pending:
      "Your email is not confirmed yet. Please check your inbox.",
  },

  admin: {
    title: "Expert management",
    filtersHeading: "Filters",
    search: "Organization, email, NEQ…",
    allStatuses: "All statuses",
    view: "View",
    pendingChanges: "Pending changes",
    empty: "No experts.",
    countTotal_one: "{{count}} expert in total",
    countTotal_other: "{{count}} experts in total",
    countMatch_one: "{{count}} expert matches the filters",
    countMatch_other: "{{count}} experts match the filters",
    noName: "(no name)",
    filterRegion: "Region",
    filterProvenance: "Origin",
    filterIssue: "Issue",
    allRegions: "All regions",
    allProvenances: "All origins",
    allIssues: "All issues",
    filter: "Filter",
    reset: "Reset",
    exportFiltered: "Export filtered results",
    exportAll: "Export all experts",
    confirmRevoke: "Revoke this invitation?",
    confirmBulkSend: "Send all unsent invitations now?",
    searchInvitation: "Search an invitation",
    invSentAt: "Sent on",
    invExpiresAt: "Expires on",
    invAllStatuses: "All statuses",
    invStatusUnsent: "Not sent",
    invStatusSent: "Sent",
    invStatusExpired: "Expired",
    columns: {
      organization: "Organization",
      email: "Email",
      status: "Status",
      registered: "Registered",
      lastLogin: "Last login",
      actionSelections: "Courses of action",
      actions: "Actions",
    },
    statuses: {
      draft: "Draft",
      submitted: "Submitted",
      validated: "Validated",
      rejected: "Rejected",
    },
    tabsLabel: "Directory sections",
    tabs: {
      experts: "Experts",
      invitations: "Pending invitations",
    },
    sendInvite: "Send an invitation",
    inviteEmail: "Email",
    inviteContactName: "Invitee name",
    language: "Invitation language",
    send: "Send",
    sendInviteSubmit: "Send invitation",
    inviteSent: "Invitation sent to {{email}}.",
    resend: "Resend",
    revoke: "Revoke",
    sendPending: "Send unsent invitations ({{count}})",
    invitationEmpty: "No pending invitations.",
    exportButton: "Export (.xlsx)",
    importExperts: "Import experts",
    importIssues: "Import issues",
    import: {
      pick: "Choose an .xlsx file",
      downloadTemplate: "Download template",
      preview: "Preview (nothing saved yet)",
      confirm: "Confirm import",
      totalRows: "Rows read",
      created: "To create",
      updated: "To update",
      skipped: "Skipped",
      expertsMatched: "Experts matched",
      pairsApplied: "Issues pre-approved",
      errors: "In error",
      applied: "Import complete.",
      downloadErrors: "Download rows in error",
      rowLabel: "Row",
      errorColumn: "Error",
      noteColumn: "Note",
      notOffered: "Pre-approved, hidden at registration",
      notOfferedHeading_one:
        "{{count}} issue is pre-approved and kept, but isn't offered at registration",
      notOfferedHeading_other:
        "{{count}} issues are pre-approved and kept, but aren't offered at registration",
      chooseAnother: "Choose another file",
      duplicates_one: "{{count}} identical duplicate ignored (row merged).",
      duplicates_other: "{{count}} identical duplicates ignored (rows merged).",
      errorsHeading_one: "{{count}} row in error will not be imported",
      errorsHeading_other: "{{count}} rows in error will not be imported",
    },
    history: {
      heading: "History",
      empty: "No event recorded.",
      submission: "Request submitted",
      changesSubmitted: "Changes submitted",
      validated: "Request validated",
      rejected: "Request rejected",
      changesValidated: "Changes validated",
      changesRejected: "Changes rejected",
      declarant: "declarant: {{name}}",
      by: "by {{name}}",
      edit: "Change saved",
      noteRemoved: "Internal note removed",
      noteAnonymized: "Anonymized note",
      noteBy: "Note by {{name}}:",
      becomes: "becomes",
      labelColon: "{{label}}:",
    },
    detail: {
      back: "Back to list",
      list: "List",
      eligibility: "Eligibility",
      onRena: "On RENA",
      renaListed: "Listed on the RENA",
      renaListedHint: "Not eligible for the directory",
      renaNotListed: "Not listed on the RENA",
      renaNotListedHint: "No impediment",
      renaUnknown: "RENA listing",
      renaUnknownHint: "Not provided",
      completeness: "Profile completeness",
      completenessMissing: "{{count}} field(s) missing",
      missing: "Missing fields",
      declarationHeading: "Declaration and origin",
      declarant: "Declarant",
      declarationDate: "Declaration date",
      signatureConsent: "Signature / consent",
      signatureConfirmed: "Signature confirmed",
      consentDisplay: "Consents to analysis",
      noConsent: "No consent",
      invitedBy: "Invited by",
      neq: "NEQ",
      submittedAgo: "Submitted {{ago}} ago",
      submittedOn: "Submitted on {{date}}",
      reviewedBy: "by {{who}}",
      enjeux: "Issues & expertise",
      preapproved: "Pre-approved by CQDD",
      hiddenPreapproved: "Pre-approved, not offered at registration",
      hiddenPreapprovedHelp:
        "The pre-approval is kept, but the issue does not appear on the expert's form.",
      isNew: "New",
      scopeChanged: "Scope changed for this issue",
      mandateChanged: "Updated",
      mandate: "Reference mandate",
      mandateYear: "Completed in {{year}}",
      mandateCount_one: "{{count}} mandate completed",
      mandateCount_other: "{{count}} mandates completed",
      objectives: "Objectives and courses of action",
      clients: "Client contact details",
      clientNoName: "Unnamed client",
      removed: "Removed by the expert",
      outsideSectors: "Outside business sectors served",
      outsideSectorsNote:
        "Outside business sectors served, not visible to the expert or to enterprises",
      orphans: "Other (issue no longer declared)",
      identityHeading: "Identity and contact details",
      profileHeading: "Organization profile",
      scope: "Service scope",
      noEnjeux: "No expertise declared.",
      validate: "Validate application",
      validateChanges: "Validate changes",
      reject: "Deny",
      rejectChanges: "Reject changes",
      rejectMessageLabel:
        "Message sent to the expert (included in the rejection email). Optional.",
      cancel: "Cancel",
      remind: "Send reminder",
      revalidate: "Re-validate",
      reminderSent: "A reminder was sent to {{name}}.",
      reminderError: "The reminder could not be sent.",
      decisionDone: "Decision recorded.",
      decisionError: "The decision could not be recorded.",
      notes: {
        title: "Internal notes",
        private: "Not visible to the expert",
        addLabel: "Add a note (internal use only).",
        add: "Add note",
        empty: "No internal notes yet.",
        unknownAuthor: "Unknown author",
        seeAll: "See all notes ({{count}})",
        delete: "Remove note",
        confirmRemove: "Remove this internal note?",
        confirmRemoveBody: "The note will be removed from the list and its removal recorded in the History.",
        removed: "Internal note removed.",
        anonymized: "Anonymized note",
        removeError: "The note could not be removed.",
        error: "The note could not be saved.",
      },
    },
  },

  invitations: {
    heading: "Expert invitations",
    help: "Invitations to claim an expert profile (CQDD administration).",
    empty: "No invitations.",
    columns: {
      email: "Email",
      source: "Source",
      status: "Status",
      created: "Created",
      expires: "Expires",
    },
    source: {
      invitation: "Invitation",
      import: "Import",
      self_registration: "Self-registration",
    },
    status: {
      revoked: "Revoked",
      used: "Accepted",
      expired: "Expired",
      sent: "Sent",
      unsent: "Not sent",
      pending: "Not sent",
    },
  },
} as const;
